import React, { useCallback, useEffect, useMemo } from 'react';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { useRefetchWhenIsSelected } from '@jira/navigation-apps__routing-hooks/src/useRefetchWhenIsSelected.tsx';
import { useDeleteChangeActions } from '@jira/global-pages__directories-filters-directory-v2-delete-filters-modal/src/controllers/delete-change-actions/index.tsx';
import type { ItemTypename } from '@jira/platform__favouriting-favourite-change-provider/src/model/types.tsx';
import {
	BUTTON,
	NAVIGATION_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	FIXED_ITEM,
	FIXED_ITEM_BUTTON,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_ONE } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import {
	SECTION_ITEM_RECENT,
	SECTION_ITEM_STARRED,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import {
	MENU_ID_FILTERS,
	MENU_ID_FILTERS_VIEW_ALL_STARRED,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { useSubscribeAndUpdateFavorites } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/services/set-favorite/index.tsx';
import { useStableItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-stable-items/index.tsx';
import { useReorderableStarredItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-reorderable-starred-items.tsx';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { NestedItemDropTarget } from '@atlassian/navigation-drag-and-drop/nested-item-drop-target';
import { useSpaStateTransition } from '@atlassian/jira-spa-state-controller/src/components/transition-state/index.tsx';

import { Nav4FilterMenuItem } from '../filter-menu-item/Nav4FilterMenuItem.tsx';
import { Nav4FilterMenuItemRefactored } from '../filter-menu-item/Nav4FilterMenuItemRefactored.tsx';
import { useFilterReorderingIds } from '../../utils/use-filter-reordering-id.tsx';
import {
	getDefaulFilterAttributes,
	getRecentAndFavouriteFilterAttributes,
	useFilterActionsAnalytics,
} from '../../utils/analytics/index.tsx';
import type {
	Nav4FiltersContent_navigationCustomisation$data,
	Nav4FiltersContent_navigationCustomisation$key,
} from './__generated__/Nav4FiltersContent_navigationCustomisation.graphql';

import QUERY, {
	type Nav4FiltersContentQuery,
} from './__generated__/Nav4FiltersContentQuery.graphql';
import type {
	Nav4FiltersContent$key,
	Nav4FiltersContent$data,
} from './__generated__/Nav4FiltersContent.graphql';

const recentMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.content.recent',

	defaultMessage: 'Recent',
	description: 'Message for recent heading in the filters menu items',
});

const starredMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.content.starred',

	defaultMessage: 'Starred',
	description: 'Message for starred heading in the filters menu items',
});

const viewAllStarred = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.content.view-all-starred',

	defaultMessage: 'View all starred items',
	description: 'Message for view all starred link in the starred filters section',
});

const viewAllStarredFilters = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.content.view-all-starred-filters',

	defaultMessage: 'View all starred filters',
	description: 'Message for view all starred filters link in the starred filters section',
});

const defaultFilters = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-filters.common.content.default-filters',

	defaultMessage: 'Default filters',
	description: 'Collapsible navigation item that contains a list of default filters',
});

const MAX_RECENT_FILTERS = 5;
export const MAX_FAVORITE_FILTERS = 15;
const VIEW_ALL_STARRED_HREF = '/jira/your-work?starred';
const VIEW_ALL_STARRED_FILTERS_HREF = '/jira/filters?sortKey=favourite&sortOrder=DESC';

type DefaultFilterIds = { id: string };

const defaultFiltersRequiringLogin: DefaultFilterIds[] = [{ id: '-1' }, { id: '-2' }, { id: '-3' }];

type Nav4FiltersContentProps = {
	queryRef: Nav4FiltersContent$key;
	navigationCustomisationFragment?: Nav4FiltersContent_navigationCustomisation$key;
};

const entityTypenames: ItemTypename[] = ['JiraCustomFilter', 'JiraSystemFilter'];

export function Nav4FiltersContent({
	queryRef,
	navigationCustomisationFragment,
}: Nav4FiltersContentProps): React.JSX.Element {
	const cloudId = useCloudId();
	const { isSelectedPath } = useSidebarNav4();
	const [{ isInitialRender }] = useSpaStateTransition();
	const isSelectedPathOnTransition = useCallback<typeof isSelectedPath>(
		(menuId) => !isInitialRender && isSelectedPath(menuId),
		[isInitialRender, isSelectedPath],
	);

	// We intentionally request 15 starred and 20 recent filters.
	// Recent filters should exclude system/default filters.
	// Then, if all 15 starred filters were recently visited, we still show 5 recent filters as well.
	const [jira, refetch] = useRefetchableFragment<Nav4FiltersContentQuery, Nav4FiltersContent$key>(
		graphql`
			fragment Nav4FiltersContent on Query
			@refetchable(queryName: "Nav4FiltersContentQuery")
			@argumentDefinitions(currentURL: { type: URL }) {
				jira @required(action: THROW) {
					favouriteFilters: favourites(
						cloudId: $cloudId
						filter: { type: FILTER, sort: { order: DESC } }
						first: 15
					) @required(action: THROW) {
						__id
						pageInfo {
							hasNextPage
						}
						edges {
							node @required(action: THROW) {
								... on JiraFilter {
									__typename
									id #-- needed for reordering (need skip)
									filterId
									name # -- needed for reordering (need skip)
									...Nav4FilterMenuItem @dangerously_unaliased_fixme
									...Nav4FilterMenuItemRefactored @dangerously_unaliased_fixme
								}
							}
						}
					}
					recentFilters: recentItems(
						cloudId: $cloudId
						currentURL: $currentURL
						types: [FILTER]
						first: 20
					) @required(action: THROW) {
						__id
						edges {
							node @required(action: THROW) {
								... on JiraCustomFilter {
									filterId
									id #-- needed for reordering (need skip)
									name # -- needed for reordering (need skip)
									...Nav4FilterMenuItem @dangerously_unaliased_fixme
									...Nav4FilterMenuItemRefactored @dangerously_unaliased_fixme
								}
							}
						}
					}
					systemFilters(cloudId: $cloudId) @required(action: THROW) {
						edges {
							node @required(action: THROW) {
								... on JiraSystemFilter {
									id #-- needed for reordering (need skip)
									filterId
									...Nav4FilterMenuItem
									...Nav4FilterMenuItemRefactored
								}
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const navigationCustomisation = useFragment(
		graphql`
			fragment Nav4FiltersContent_navigationCustomisation on SettingsNavigationCustomisation {
				sidebar {
					nodes {
						# It is necessary to suppress the lint rule, because these ARE used by useReorderableStarredItems
						# eslint-disable-next-line @atlassian/relay/unused-fields
						menuId
						# eslint-disable-next-line @atlassian/relay/unused-fields
						visible
					}
				}
			}
		`,
		navigationCustomisationFragment,
	);

	useRefetchWhenIsSelected({
		cloudId,
		isSelectedPath: isSelectedPathOnTransition,
		QUERY,
		refetch,
		menuId: MENU_ID_FILTERS,
	});

	const refetchOnChange = useCallback(
		({ onComplete }: { onComplete: () => void }) => {
			refetch({ cloudId }, { fetchPolicy: 'store-and-network', onComplete });
		},
		[cloudId, refetch],
	);

	useSubscribeAndUpdateFavorites({
		entityTypename: entityTypenames,
		favouriteConnectionId: jira?.jira.favouriteFilters.__id || '',
		recentConnectionId: jira?.jira.recentFilters.__id || '',
		refetch: refetchOnChange,
	});

	const { subscribe, unsubscribe } = useDeleteChangeActions();

	// allows the sidebar to reflect filter changes when a filter is deleted from the filters directory page
	useEffect(() => {
		subscribe(refetchOnChange);
		return () => {
			unsubscribe(refetchOnChange);
		};
	}, [refetchOnChange, subscribe, unsubscribe]);

	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		return (
			<Nav4FiltersContentInnerNew
				jira={jira}
				refetchOnChange={refetchOnChange}
				navigationCustomisation={navigationCustomisation}
			/>
		);
	}

	return <Nav4FiltersContentInnerOld jira={jira.jira} refetchOnChange={refetchOnChange} />;
}

type Nav4FiltersContentInnerOldProps = {
	jira: Nav4FiltersContent$data['jira'];
	// eslint-disable-next-line jira/react/handler-naming
	refetchOnChange: ({ onComplete }: { onComplete: () => void }) => void;
};

const Nav4FiltersContentInnerOld = (props: Nav4FiltersContentInnerOldProps) => {
	const {
		jira: { favouriteFilters, recentFilters, systemFilters },
		refetchOnChange,
	} = props;
	const { getStableItems } = useStableItems();

	const { formatMessage } = useIntl();
	const starred = formatMessage(starredMessage);
	const recent = formatMessage(recentMessage);
	const sendFilterAnalytics = useFilterActionsAnalytics();

	const sendFavouriteFilterAnalytics = useCallback(
		(isCustom: boolean, filterId: string | undefined) => {
			if (!filterId) return;
			sendFilterAnalytics(
				NAVIGATION_ITEM,
				getRecentAndFavouriteFilterAttributes({
					isCustom,
					filterId,
					section: SECTION_ITEM_STARRED,
				}),
			);
		},
		[sendFilterAnalytics],
	);

	const favorites = useMemo(() => {
		const edges = favouriteFilters.edges?.filter(Boolean) ?? [];
		const ids: string[] = [];
		const list: React.JSX.Element[] = [];

		for (const edge of edges) {
			if (edge.node.filterId) {
				ids.push(edge.node.filterId);
				const isCustom = edge.node.__typename === 'JiraCustomFilter';
				const filterId = edge.node.filterId;
				list.push(
					<Nav4FilterMenuItem
						key={filterId}
						queryRef={edge.node}
						onRefetch={refetchOnChange}
						onClick={() => sendFavouriteFilterAnalytics(isCustom, filterId)}
					/>,
				);
			}
		}

		return {
			idSet: new Set(ids),
			list: list.slice(0, MAX_FAVORITE_FILTERS),
			hasItems: Boolean(list.length),
			hasMore: favouriteFilters.pageInfo?.hasNextPage || list.length > MAX_FAVORITE_FILTERS,
		};
	}, [
		favouriteFilters.edges,
		favouriteFilters.pageInfo?.hasNextPage,
		refetchOnChange,
		sendFavouriteFilterAnalytics,
	]);

	const sendRecentFilterAnalytics = useCallback(
		(filterId: string | undefined) => {
			if (!filterId) return;
			sendFilterAnalytics(
				NAVIGATION_ITEM,
				getRecentAndFavouriteFilterAttributes({
					isCustom: true,
					filterId,
					section: SECTION_ITEM_RECENT,
				}),
			);
		},
		[sendFilterAnalytics],
	);

	const recentItems = useMemo(() => {
		const edges = recentFilters.edges?.filter(Boolean) ?? [];
		const filteredNodes: (typeof edges)[number]['node'][] = [];

		for (const edge of edges) {
			if (edge.node.filterId && !favorites.idSet.has(edge.node.filterId)) {
				filteredNodes.push(edge.node);
				if (filteredNodes.length >= MAX_RECENT_FILTERS) break;
			}
		}

		const stableOrderedNodes = getStableItems({ nodes: filteredNodes, idKey: 'filterId' });

		const list: React.JSX.Element[] = [];
		for (const node of stableOrderedNodes) {
			if (node.filterId) {
				const filterId = node.filterId;
				list.push(
					<Nav4FilterMenuItem
						key={filterId}
						queryRef={node}
						onRefetch={refetchOnChange}
						onClick={() => sendRecentFilterAnalytics(filterId)}
					/>,
				);
			}
		}

		return { list, hasItems: Boolean(list.length) };
	}, [
		recentFilters.edges,
		getStableItems,
		favorites.idSet,
		refetchOnChange,
		sendRecentFilterAnalytics,
	]);

	const isAnonymous = useIsAnonymous();

	const sendDefaultFilterAnalytics = useCallback(
		(filterId: string | undefined) => {
			if (!filterId) return;
			sendFilterAnalytics(NAVIGATION_ITEM, getDefaulFilterAttributes({ filterId }));
		},
		[sendFilterAnalytics],
	);

	const defaultItems = useMemo(() => {
		const filterIds: string[] = [];
		const edges = systemFilters.edges ?? [];
		const list: React.JSX.Element[] = [];

		for (const edge of edges) {
			if (
				edge?.node?.filterId &&
				!favorites.idSet.has(edge.node.filterId) &&
				(!isAnonymous ||
					!defaultFiltersRequiringLogin.find((filter) => filter.id === edge.node?.filterId))
			) {
				filterIds.push(edge.node.filterId);
				const filterId = edge.node.filterId;
				list.push(
					<Nav4FilterMenuItem
						key={filterId}
						queryRef={edge.node}
						onRefetch={refetchOnChange}
						onClick={() => sendDefaultFilterAnalytics(filterId)}
					/>,
				);
			}
		}

		return { list, hasItems: Boolean(list.length), filterIds };
	}, [
		systemFilters.edges,
		favorites.idSet,
		isAnonymous,
		refetchOnChange,
		sendDefaultFilterAnalytics,
	]);

	const sendViewAllStarredAnalytics = useCallback(() => {
		sendFilterAnalytics(NAVIGATION_ITEM, {
			level: LEVEL_ONE,
			componentType: FIXED_ITEM,
			itemId: 'viewAllStarred',
		});
	}, [sendFilterAnalytics]);

	const sendDefaultFilterDropdownAnalytics = useCallback(() => {
		sendFilterAnalytics(BUTTON, {
			level: LEVEL_ONE,
			componentType: FIXED_ITEM_BUTTON,
			itemId: 'defaultFilters',
		});
	}, [sendFilterAnalytics]);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendDefaultFilterDropdownAnalytics();
			}
		},
		[sendDefaultFilterDropdownAnalytics],
	);
	return (
		<>
			{favorites.hasItems && (
				<>
					<MenuListItem>
						<MenuSection>
							<MenuSectionHeading>{starred}</MenuSectionHeading>
							<MenuList>{favorites.list}</MenuList>
						</MenuSection>
					</MenuListItem>
					{favouriteFilters.pageInfo?.hasNextPage && (
						<Nav4MenuLinkItem
							href={
								expVal('jjj5-cosmos-navigation', 'isEnabled', false)
									? VIEW_ALL_STARRED_FILTERS_HREF
									: VIEW_ALL_STARRED_HREF
							}
							menuId={MENU_ID_FILTERS_VIEW_ALL_STARRED}
							onClick={sendViewAllStarredAnalytics}
						>
							{formatMessage(
								expVal('jjj5-cosmos-navigation', 'isEnabled', false)
									? viewAllStarredFilters
									: viewAllStarred,
							)}
						</Nav4MenuLinkItem>
					)}
				</>
			)}
			{recentItems.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{recent}</MenuSectionHeading>
						<MenuList>{recentItems.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
			{defaultItems.hasItems && (
				<ExpandableMenuItem isDefaultExpanded onExpansionToggle={onExpansionToggle}>
					<ExpandableMenuItemTrigger interactionName="nav4-sidebar-filters-default-filters">
						{formatMessage(defaultFilters)}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>{defaultItems.list}</ExpandableMenuItemContent>
				</ExpandableMenuItem>
			)}
		</>
	);
};

type Nav4FiltersContentInnerNewProps = {
	jira: Nav4FiltersContent$data;
	// eslint-disable-next-line jira/react/handler-naming
	refetchOnChange: ({ onComplete }: { onComplete: () => void }) => void;
	navigationCustomisation?: Nav4FiltersContent_navigationCustomisation$data | null;
};

const Nav4FiltersContentInnerNew = (props: Nav4FiltersContentInnerNewProps) => {
	const { jira: jiraData, refetchOnChange, navigationCustomisation } = props;

	const { favouriteFilters, recentFilters, systemFilters } = jiraData.jira;
	const { getStableItems } = useStableItems();
	const { formatMessage } = useIntl();
	const starred = formatMessage(starredMessage);
	const recent = formatMessage(recentMessage);
	const sendFilterAnalytics = useFilterActionsAnalytics();

	const { entityAri, ownerAri, group } = useFilterReorderingIds();
	const filtersOrder = navigationCustomisation || null;

	type FilterEdge = NonNullable<NonNullable<NonNullable<typeof favouriteFilters>['edges']>[number]>;
	type FilterNode = NonNullable<FilterEdge['node']>;

	const sendFavouriteFilterAnalytics = useCallback(
		(isCustom: boolean, filterId: string | undefined) => {
			if (!filterId) return;
			sendFilterAnalytics(
				NAVIGATION_ITEM,
				getRecentAndFavouriteFilterAttributes({
					isCustom,
					filterId,
					section: SECTION_ITEM_STARRED,
				}),
			);
		},
		[sendFilterAnalytics],
	);

	const filterNodes: FilterNode[] = useMemo(() => {
		const edges = favouriteFilters.edges ?? [];
		const nodes: FilterNode[] = [];

		for (const edge of edges) {
			if (edge != null && edge !== undefined && edge.node != null && edge.node !== undefined) {
				nodes.push(edge.node);
			}
		}

		return nodes;
	}, [favouriteFilters.edges]);

	const renderMenuItem = useCallback(
		(
			edge: FilterNode,
			currentItems: Array<{ id: string; label: string }>,
			currentOrder: Array<{ menuId: string; visible: boolean }>,
			item: { id: string; label: string },
		) => {
			const isCustom = edge.__typename === 'JiraCustomFilter';
			const edgeId = edge.id;
			return (
				<Nav4FilterMenuItemRefactored
					key={edgeId}
					queryRef={edge}
					onRefetch={refetchOnChange}
					onClick={() => sendFavouriteFilterAnalytics(isCustom, edgeId)}
					currentItems={currentItems}
					currentOrder={currentOrder}
					item={item}
				/>
			);
		},
		[refetchOnChange, sendFavouriteFilterAnalytics],
	);

	const favorites = useReorderableStarredItems<FilterNode>({
		favourites: filterNodes,
		lastOrder: filtersOrder?.sidebar?.nodes,
		getId: (item) => item.id || '',
		getLabel: (item) => item?.name?.toString() || '',
		getHasNextPage: () => Boolean(favouriteFilters?.pageInfo?.hasNextPage),
		renderMenuItem,
		maxItems: MAX_FAVORITE_FILTERS,
	});

	const sendRecentFilterAnalytics = useCallback(
		(filterId: string | undefined) => {
			if (!filterId) return;
			sendFilterAnalytics(
				NAVIGATION_ITEM,
				getRecentAndFavouriteFilterAttributes({
					isCustom: true,
					filterId,
					section: SECTION_ITEM_RECENT,
				}),
			);
		},
		[sendFilterAnalytics],
	);

	const recentItems = useMemo(() => {
		const edges = recentFilters.edges?.filter(Boolean) ?? [];
		const filteredNodes: (typeof edges)[number]['node'][] = [];

		for (const edge of edges) {
			if (edge.node.id && !favorites.idSet.has(edge.node.id)) {
				filteredNodes.push(edge.node);
				if (filteredNodes.length >= MAX_RECENT_FILTERS) break;
			}
		}

		const stableOrderedNodes = getStableItems({ nodes: filteredNodes, idKey: 'filterId' });

		const list: React.JSX.Element[] = [];
		for (const node of stableOrderedNodes) {
			if (node.filterId) {
				const filterId = node.filterId;
				list.push(
					<Nav4FilterMenuItemRefactored
						key={filterId}
						queryRef={node}
						onRefetch={refetchOnChange}
						onClick={() => sendRecentFilterAnalytics(filterId)}
					/>,
				);
			}
		}

		return { list, hasItems: Boolean(list.length) };
	}, [
		recentFilters.edges,
		getStableItems,
		favorites.idSet,
		refetchOnChange,
		sendRecentFilterAnalytics,
	]);

	const isAnonymous = useIsAnonymous();

	const sendDefaultFilterAnalytics = useCallback(
		(filterId: string | undefined) => {
			if (!filterId) return;
			sendFilterAnalytics(NAVIGATION_ITEM, getDefaulFilterAttributes({ filterId }));
		},
		[sendFilterAnalytics],
	);

	const defaultItems = useMemo(() => {
		const filterIds: string[] = [];
		const edges = systemFilters.edges ?? [];
		const list: React.JSX.Element[] = [];

		for (const edge of edges) {
			if (
				edge?.node?.id &&
				edge?.node?.filterId &&
				!favorites.idSet.has(edge.node.id) &&
				(!isAnonymous ||
					!defaultFiltersRequiringLogin.find((filter) => filter.id === edge.node?.id))
			) {
				filterIds.push(edge.node.id);
				const filterId = edge.node.filterId;
				list.push(
					<Nav4FilterMenuItemRefactored
						key={filterId}
						queryRef={edge.node}
						onRefetch={refetchOnChange}
						onClick={() => sendDefaultFilterAnalytics(filterId)}
					/>,
				);
			}
		}

		return { list, hasItems: Boolean(list.length), filterIds };
	}, [
		systemFilters.edges,
		favorites.idSet,
		isAnonymous,
		refetchOnChange,
		sendDefaultFilterAnalytics,
	]);

	const sendViewAllStarredAnalytics = useCallback(() => {
		sendFilterAnalytics(NAVIGATION_ITEM, {
			level: LEVEL_ONE,
			componentType: FIXED_ITEM,
			itemId: 'viewAllStarred',
		});
	}, [sendFilterAnalytics]);

	const sendDefaultFilterDropdownAnalytics = useCallback(() => {
		sendFilterAnalytics(BUTTON, {
			level: LEVEL_ONE,
			componentType: FIXED_ITEM_BUTTON,
			itemId: 'defaultFilters',
		});
	}, [sendFilterAnalytics]);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendDefaultFilterDropdownAnalytics();
			}
		},
		[sendDefaultFilterDropdownAnalytics],
	);

	return (
		<>
			{favorites.hasItems && (
				<>
					<MenuListItem>
						<MenuSection>
							<MenuSectionHeading>{starred}</MenuSectionHeading>
							<MenuList>
								<NestedItemDropTarget
									currentItems={favorites.currentItems}
									currentOrder={favorites.currentOrder}
									entityAri={entityAri}
									group={group}
									ownerAri={ownerAri}
								>
									{favorites.list}
								</NestedItemDropTarget>
							</MenuList>
						</MenuSection>
					</MenuListItem>
					{favouriteFilters.pageInfo?.hasNextPage && (
						<Nav4MenuLinkItem
							href={
								expVal('jjj5-cosmos-navigation', 'isEnabled', false)
									? VIEW_ALL_STARRED_FILTERS_HREF
									: VIEW_ALL_STARRED_HREF
							}
							menuId={MENU_ID_FILTERS_VIEW_ALL_STARRED}
							onClick={sendViewAllStarredAnalytics}
						>
							{formatMessage(
								expVal('jjj5-cosmos-navigation', 'isEnabled', false)
									? viewAllStarredFilters
									: viewAllStarred,
							)}
						</Nav4MenuLinkItem>
					)}
				</>
			)}
			{recentItems.hasItems && (
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{recent}</MenuSectionHeading>
						<MenuList>{recentItems.list}</MenuList>
					</MenuSection>
				</MenuListItem>
			)}
			{defaultItems.hasItems && (
				<ExpandableMenuItem isDefaultExpanded onExpansionToggle={onExpansionToggle}>
					<ExpandableMenuItemTrigger interactionName="nav4-sidebar-filters-default-filters">
						{formatMessage(defaultFilters)}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>{defaultItems.list}</ExpandableMenuItemContent>
				</ExpandableMenuItem>
			)}
		</>
	);
};
